import { StrictMode, Suspense, useEffect, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { ErrorScreen, NotFoundPage, RootBoundary } from './components/ErrorScreen';
import { chatIdFrom, useOpenChatId } from './lib/chatRoute';
import { AppProvider, useApp } from './lib/app-context';
import { ChatProvider } from './lib/chat-context';
import { startInstallWatch } from './lib/install';
import { trackLayout } from './lib/layout';
import { trackVisualViewport } from './lib/viewport';
import { eagerPage, lazyPage, PageBoundary, prefetchPages } from './lib/lazy-pages';
import { Splash } from './components/Loading';
import './styles/tokens.css';
import './styles/app.css';

// Chats and the shell are the first screen after sign-in: their own files, downloaded from the start (so the
// sign-in pages do not carry them). Every other page is its own file too, loaded when opened and prefetched in
// the background (lib/lazy-pages).
const Shell = eagerPage(() => import('./pages/Shell'), 'Shell');
const LinkPage = lazyPage(() => import('./pages/LinkPage'), 'LinkPage');
const ChannelLinkPage = lazyPage(() => import('./pages/ChannelLinkPage'), 'ChannelLinkPage');
const DisabledModule = eagerPage(() => import('./pages/Shell'), 'DisabledModule');
const ConversationView = eagerPage(() => import('./pages/Messages'), 'ConversationView');
const MessagesHome = eagerPage(() => import('./pages/Messages'), 'MessagesHome');
const LoginPage = lazyPage(() => import('./pages/AuthPages'), 'LoginPage');
const RegisterPage = lazyPage(() => import('./pages/AuthPages'), 'RegisterPage');
const ForgotPasswordPage = lazyPage(() => import('./pages/AccountPages'), 'ForgotPasswordPage');
const ResetPasswordPage = lazyPage(() => import('./pages/AccountPages'), 'ResetPasswordPage');
const VerifyEmailPage = lazyPage(() => import('./pages/AccountPages'), 'VerifyEmailPage');
const GuardianPage = lazyPage(() => import('./pages/GuardianPage'), 'GuardianPage');
const OnboardingPage = lazyPage(() => import('./pages/OnboardingPage'), 'OnboardingPage');
const SettingsPage = lazyPage(() => import('./pages/SettingsPage'), 'SettingsPage');
const AdminPage = lazyPage(() => import('./pages/AdminPage'), 'AdminPage');
const FamilyLinkPage = lazyPage(() => import('./components/FamilyCentre'), 'FamilyLinkPage');
const SpacesPage = lazyPage(() => import('./pages/SpacesPage'), 'SpacesPage');
const StarredPage = lazyPage(() => import('./pages/StarredPage'), 'StarredPage');
const RemindersPage = lazyPage(() => import('./pages/RemindersPage'), 'RemindersPage');
const BroadcastsPage = lazyPage(() => import('./pages/BroadcastsPage'), 'BroadcastsPage');
const MarketPage = lazyPage(() => import('./pages/MarketPage'), 'MarketPage');
const FeedbackPage = lazyPage(() => import('./pages/FeedbackPage'), 'FeedbackPage');
const WhatsNewPage = lazyPage(() => import('./pages/WhatsNewPage'), 'WhatsNewPage');
const ReligiousPage = lazyPage(() => import('./pages/ReligiousPage'), 'ReligiousPage');
const MarketAppPage = lazyPage(() => import('./pages/MarketPage'), 'MarketAppPage');
const MarketDetailPage = lazyPage(() => import('./pages/MarketPage'), 'MarketDetailPage');
const MarketDeveloperPage = lazyPage(() => import('./pages/MarketPage'), 'MarketDeveloperPage');
const DeveloperConsolePage = lazyPage(() => import('./pages/DeveloperConsole'), 'DeveloperConsolePage');
const AppsHome = lazyPage(() => import('./pages/AppsHome'), 'AppsHome');
const PlansPage = lazyPage(() => import('./pages/PaymentsPage'), 'PlansPage');
const PaymentReturnPage = lazyPage(() => import('./pages/PaymentsPage'), 'PaymentReturnPage');
const BookPage = lazyPage(() => import('./pages/BookPage'), 'BookPage');
const ShopPage = lazyPage(() => import('./pages/ShopPage'), 'ShopPage');
const InvitePage = lazyPage(() => import('./pages/InvitePage'), 'InvitePage');
const ImportsPage = lazyPage(() => import('./pages/ImportsPage'), 'ImportsPage');
const ChannelsPage = lazyPage(() => import('./pages/ChannelsPage'), 'ChannelsPage');
const PlannerPage = lazyPage(() => import('./pages/PlannerPage'), 'PlannerPage');
const LearnPage = lazyPage(() => import('./pages/LearnPage'), 'LearnPage');
const JoinEduPage = lazyPage(() => import('./pages/JoinEduPage'), 'JoinEduPage');
const EduPage = lazyPage(() => import('./edu/EduPage'), 'EduPage');
const JoinGroupPage = lazyPage(() => import('./pages/JoinGroupPage'), 'JoinGroupPage');
const JoinSpacePage = lazyPage(() => import('./pages/JoinSpacePage'), 'JoinSpacePage');
const CallsPage = lazyPage(() => import('./components/CallUi'), 'CallsPage');
const AiPage = lazyPage(() => import('./components/AssistantUi'), 'AiPage');
const LinkDevicePage = lazyPage(() => import('./components/QrLogin'), 'LinkDevicePage');
const MeydanPage = lazyPage(() => import('./meydan/MeydanPage'), 'MeydanPage');

/** requireAuth: true = signed in only, false = signed out only, 'any' = both (email links). */
function Gate({ children, requireAuth }: { children: ReactNode; requireAuth: boolean | 'any' }) {
  const { me, loading, config } = useApp();
  const here = useLocation();
  // Same start-up screen as index.html: no bare "Loading…" text while the session is checked.
  if (loading) return <Splash />;
  // The service could not be reached at start-up (no connection, or it is being updated): retried by itself.
  if (!config) return <ErrorScreen kind={navigator.onLine ? 'unavailable' : 'offline'} page />;
  if (requireAuth === 'any') return <>{children}</>;
  // A group invite or a QR sign-in link survives signing in: the login page returns there (fragment included).
  if (requireAuth && !me) {
    const keep = here.pathname === '/join' || here.pathname === '/call' || here.pathname === '/link' || here.pathname === '/aile-bagla';
    return <Navigate to="/login" replace state={keep ? { from: `${here.pathname}${here.hash}` } : undefined} />;
  }
  if (!requireAuth && me) return <Navigate to={me.onboardingCompleted ? '/' : '/onboarding'} replace />;
  return <>{children}</>;
}

/** Route changes start at the top, like a page load (a long form would otherwise leave the next page scrolled). */
function ScrollReset() {
  const { pathname } = useLocation();
  // Block body: newer browsers return a Promise from scrollTo, which must not become the effect cleanup.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/** Messenger-first: land on chats when messaging is enabled. */
function HomeRedirect() {
  const { config } = useApp();
  return <Navigate to={config?.flags.messaging ? '/messages' : '/spaces'} replace />;
}

function EduRoute() {
  const { config } = useApp();
  return config?.flags.edu ? <EduPage /> : <DisabledModule labelKey="nav.edu" />;
}

function LearnRoute() {
  const { config } = useApp();
  return config?.flags.learning_hub ? <LearnPage /> : <DisabledModule labelKey="nav.learn" />;
}

function CalendarRoute() {
  const { config } = useApp();
  return config?.flags.calendar || config?.flags.tasks ? <PlannerPage /> : <DisabledModule labelKey="nav.calendar" />;
}

function DiscoverRoute() {
  const { config } = useApp();
  return config?.flags.channels || config?.flags.stories ? <ChannelsPage /> : <DisabledModule labelKey="nav.discover" />;
}

/** /messages and /messages/<id>: the open chat (kept in the history entry, see lib/chatRoute) or the chats home. */
function MessagesRoute() {
  const { config } = useApp();
  const chat = useOpenChatId();
  if (!config?.flags.messaging) return <DisabledModule labelKey="nav.messages" />;
  // Keyed by chat: each chat starts with its own composer, reply and edit state.
  return chat ? <ConversationView key={chat} chatId={chat} /> : <MessagesHome />;
}

function App() {
  const location = useLocation();
  // Opening another chat is a new page for error recovery, although the address stays /messages.
  const pageKey = `${location.pathname}#${chatIdFrom(location) ?? ''}`;
  return (
    <PageBoundary resetKey={pageKey}>
      <Routes>
        <Route path="/login" element={<Gate requireAuth={false}><LoginPage /></Gate>} />
        <Route path="/register" element={<Gate requireAuth={false}><RegisterPage /></Gate>} />
        <Route path="/forgot-password" element={<Gate requireAuth={false}><ForgotPasswordPage /></Gate>} />
        <Route path="/reset-password" element={<Gate requireAuth="any"><ResetPasswordPage /></Gate>} />
        <Route path="/verify-email" element={<Gate requireAuth="any"><VerifyEmailPage /></Gate>} />
        <Route path="/veli-onayi" element={<Gate requireAuth="any"><GuardianPage /></Gate>} />
        <Route path="/onboarding" element={<Gate requireAuth><OnboardingPage /></Gate>} />
        {/* web.dijitalapp.com/kanal/<slug>: a channel's own link (/channel/<slug> sends on to it); open to visitors too. */}
        <Route path="/kanal/:slug" element={<Gate requireAuth="any"><Suspense fallback={<Splash />}><ChannelLinkPage /></Suspense></Gate>} />
        <Route path="/channel/:slug" element={<Gate requireAuth="any"><Suspense fallback={<Splash />}><ChannelLinkPage /></Suspense></Gate>} />
        {/* dijitalapp.com/@name: a person, group or channel; open to visitors too (they are asked to sign in). */}
        <Route path="/:at" element={<Gate requireAuth="any"><Suspense fallback={<Splash />}><LinkPage /></Suspense></Gate>} />
        {/* The shell's own file is usually there already; if not, the start-up screen stays a moment longer. */}
        <Route element={<Gate requireAuth><Suspense fallback={<Splash />}><Shell /></Suspense></Gate>}>
          <Route index element={<HomeRedirect />} />
          <Route path="spaces" element={<SpacesPage />} />
          <Route path="spaces/:id" element={<SpacesPage />} />
          <Route path="spaces/:id/:section" element={<SpacesPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/:section" element={<SettingsPage />} />
          <Route path="admin" element={<AdminPage />} />
          <Route path="admin/:tab" element={<AdminPage />} />
          <Route path="messages" element={<MessagesRoute />} />
          <Route path="messages/new" element={<MessagesRoute />} />
          {/* A link to one chat (a notification, elsewhere): the same view as /messages, so when the address then
              becomes /messages the open chat is kept as it is (not built again). */}
          <Route path="messages/:id" element={<MessagesRoute />} />
          <Route path="join" element={<JoinGroupPage />} />
          <Route path="call" element={<JoinGroupPage />} />
          <Route path="space-join" element={<JoinSpacePage />} />
          <Route path="starred" element={<StarredPage />} />
          <Route path="reminders" element={<RemindersPage />} />
          <Route path="broadcasts" element={<BroadcastsPage />} />
          <Route path="market" element={<MarketPage />} />
          <Route path="market/:id" element={<MarketDetailPage />} />
          <Route path="market/developers/:slug" element={<MarketDeveloperPage />} />
          <Route path="market/developer-program" element={<DeveloperConsolePage />} />
          <Route path="feedback" element={<FeedbackPage />} />
          <Route path="yenilikler" element={<WhatsNewPage />} />
          <Route path="onemli-gunler" element={<ReligiousPage />} />
          <Route path="onemli-gunler/:section/:id" element={<ReligiousPage />} />
          <Route path="apps" element={<AppsHome />} />
          <Route path="apps/:id" element={<MarketAppPage />} />
          <Route path="plans" element={<PlansPage />} />
          <Route path="payments/:id" element={<PaymentReturnPage />} />
          <Route path="book/:slug" element={<BookPage />} />
          <Route path="shop/:slug" element={<ShopPage />} />
          <Route path="invite" element={<InvitePage />} />
          <Route path="aile-bagla" element={<FamilyLinkPage />} />
          <Route path="imports" element={<ImportsPage />} />
          <Route path="imports/:id" element={<ImportsPage />} />
          <Route path="calls" element={<CallsPage />} />
          <Route path="discover" element={<DiscoverRoute />} />
          <Route path="discover/:section" element={<DiscoverRoute />} />
          {/* Meydan, the social feed in its views; profiles /meydan/@name, posts /meydan/p/:id, hashtags /meydan/t/:tag. */}
          <Route path="meydan" element={<MeydanPage />} />
          <Route path="meydan/p/:id" element={<MeydanPage />} />
          <Route path="meydan/t/:tag" element={<MeydanPage />} />
          <Route path="meydan/:handle" element={<MeydanPage />} />
          <Route path="calendar" element={<CalendarRoute />} />
          <Route path="calendar/:tab" element={<CalendarRoute />} />
          <Route path="learn" element={<LearnRoute />} />
          <Route path="learn/:section" element={<LearnRoute />} />
          <Route path="learn/class/:classId" element={<LearnRoute />} />
          <Route path="edu-join" element={<JoinEduPage />} />
          {/* The Eğitim module: /egitim, /egitim/cocuk/:linkId, /egitim/kurum/:orgId/:tab, /egitim/yeni, /egitim/davet#token. */}
          <Route path="egitim/*" element={<EduRoute />} />
          <Route path="ai" element={<AiPage />} />
          <Route path="link" element={<LinkDevicePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </PageBoundary>
  );
}

startInstallWatch();
trackLayout();
trackVisualViewport();
prefetchPages();

// A browser too old for the app already shows browser-check.js's message: do not start over it.
if (!(window as Window & { __daUnsupported?: string[] }).__daUnsupported) createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RootBoundary>
    <AppProvider>
      <ChatProvider>
        <BrowserRouter>
          <ScrollReset />
          <App />
        </BrowserRouter>
      </ChatProvider>
    </AppProvider>
    </RootBoundary>
  </StrictMode>,
);
